// Neo's inline charts (cloud ADR 0230) on the client: decoding the chart
// records a turn reports, and the pure pieces NeoChart draws from — a slot
// grid as chart points in the viewer's units, the one principal stat chip,
// titles, the window label and the bar labels. No DOM, so cloud/web's Node
// vitest covers it.
//
// A chart carries values in the boat's stored marine-metric units; every number
// is converted here, with the viewer's own unit settings (ADR 0195), before a
// chart component sees it.

import {
  NEO_CHART_ID,
  NEO_CHART_KINDS,
  NEO_CHARTS_PER_ANSWER_MAX,
  formatDurationSeconds,
  isHourMeter,
  isSecondsUnit,
  neoChartTitle,
  type DurationScale,
  type NeoChart,
  type NeoChartChip,
  type NeoChartKind,
  type NeoChartSlots,
  type NeoChartTrendSeries,
  type NeoChartWindow,
  type UnitSettings,
} from '@neomaris/protocol';

import type { SeriesPoint } from '../components/charts';
import { categoryFor, formatDisplay, toDisplay, type UnitCategory } from './displayUnits';

export type { NeoChart } from '@neomaris/protocol';

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

const isNumberOrNull = (v: unknown): boolean =>
  v === null || (typeof v === 'number' && Number.isFinite(v));

function validSlots(value: unknown): boolean {
  return (
    isObject(value) &&
    typeof value.start === 'string' &&
    typeof value.stepSeconds === 'number' &&
    value.stepSeconds > 0 &&
    Array.isArray(value.mean) &&
    value.mean.every(isNumberOrNull)
  );
}

function validSnapshot(kind: NeoChartKind, snapshot: Record<string, unknown>): boolean {
  if (kind === 'trend') {
    return (
      Array.isArray(snapshot.series) &&
      snapshot.series.length > 0 &&
      snapshot.series.every(
        (s) =>
          isObject(s) &&
          typeof s.path === 'string' &&
          (s.label === undefined || typeof s.label === 'string') &&
          validSlots(s.slots)
      )
    );
  }
  if (kind === 'readouts') {
    return (
      Array.isArray(snapshot.items) &&
      snapshot.items.length > 0 &&
      snapshot.items.every(
        (i) =>
          isObject(i) &&
          typeof i.path === 'string' &&
          (i.label === undefined || typeof i.label === 'string') &&
          isNumberOrNull(i.value) &&
          (i.trend === null || validSlots(i.trend))
      )
    );
  }
  return (
    (snapshot.unit === 'h' || snapshot.unit === 'nm') &&
    Array.isArray(snapshot.bars) &&
    snapshot.bars.length > 0 &&
    snapshot.bars.every((b) => isObject(b) && typeof b.key === 'string' && isNumberOrNull(b.value))
  );
}

/**
 * The charts a turn reported, decoded defensively: a record that is not a
 * chart is dropped, never half-drawn. On a boat page the chat is about that
 * boat, so a chart of any other boat is dropped too; with no boatId (the
 * general chat, legitimately multi-boat) nothing is filtered.
 */
export function parseNeoCharts(raw: unknown, boatId?: string): NeoChart[] {
  if (!Array.isArray(raw)) return [];
  const out: NeoChart[] = [];
  for (const c of raw) {
    if (out.length >= NEO_CHARTS_PER_ANSWER_MAX) break;
    if (!isObject(c) || typeof c.id !== 'string' || !NEO_CHART_ID.test(c.id)) continue;
    if (out.some((x) => x.id === c.id)) continue;
    const { spec, snapshot } = c;
    if (!isObject(spec) || !isObject(snapshot)) continue;
    const kind = spec.kind as NeoChartKind;
    if (!NEO_CHART_KINDS.includes(kind) || snapshot.kind !== kind) continue;
    if (typeof spec.boatId !== 'string') continue;
    if (boatId !== undefined && spec.boatId !== boatId) continue;
    const window = spec.window;
    if (!isObject(window) || typeof window.from !== 'string' || typeof window.to !== 'string') {
      continue;
    }
    if (!validSnapshot(kind, snapshot)) continue;
    out.push(c as unknown as NeoChart);
  }
  return out;
}

/**
 * A slot grid as chart points in the viewer's units. A null slot stays a gap
 * the line breaks across — never bridged, never zero. Each point sits at the
 * middle of its slot.
 */
export function slotsToPoints(
  slots: NeoChartSlots,
  unit: string | null,
  category: UnitCategory | undefined,
  settings: UnitSettings,
  opts: { band?: boolean; scale?: DurationScale } = {}
): SeriesPoint[] {
  const start = Date.parse(slots.start);
  const step = slots.stepSeconds * 1000;
  // A duration draws in its chart's one unit (TOM-742).
  const scale = opts.scale;
  const conv = (v: number) =>
    scale !== undefined && isSecondsUnit(unit)
      ? v / scale.seconds
      : toDisplay(v, unit, category, settings).value;
  return slots.mean.map((v, i) => {
    const lo = slots.min?.[i];
    const hi = slots.max?.[i];
    const band =
      opts.band === true && lo !== undefined && lo !== null && hi !== undefined && hi !== null;
    return {
      t: start + i * step + step / 2,
      v: v === null ? null : conv(v),
      ...(band ? { lo: conv(lo), hi: conv(hi) } : {}),
    };
  });
}

const CHIP_LABEL: Record<NeoChartChip, string> = {
  last: 'Last',
  min: 'Min',
  max: 'Max',
  mean: 'Avg',
  change: 'Change',
};

export interface NeoChartChipText {
  label: string;
  text: string;
  unit: string;
}

/**
 * The trend's one principal number, formatted in the viewer's units. A change
 * converts both ends before subtracting — a temperature conversion is affine, so
 * converting the difference itself would add 32 °F.
 */
export function trendChip(
  series: NeoChartTrendSeries,
  chip: NeoChartChip,
  settings: UnitSettings
): NeoChartChipText | null {
  const category = categoryFor(series.path, series.unit);
  const label = CHIP_LABEL[chip];
  if (chip === 'change') {
    if (series.first === null || series.last === null) return null;
    // A duration's change reads as a duration too (TOM-742).
    if (isSecondsUnit(series.unit) && !isHourMeter(series.path, series.unit)) {
      const delta = series.last - series.first;
      const text = formatDurationSeconds(delta);
      return { label, text: delta > 0 ? `+${text}` : text, unit: '' };
    }
    const a = toDisplay(series.first, series.unit, category, settings);
    const b = toDisplay(series.last, series.unit, category, settings);
    const decimals = (
      formatDisplay(series.last, series.unit, category, settings).text.split('.')[1] ?? ''
    ).length;
    const delta = b.value - a.value;
    const text = `${delta >= 0 ? '+' : '−'}${Math.abs(delta).toFixed(decimals)}`;
    return { label, text, unit: b.unit };
  }
  const value =
    chip === 'last'
      ? series.last
      : chip === 'min'
        ? (series.stats?.min ?? null)
        : chip === 'max'
          ? (series.stats?.max ?? null)
          : (series.stats?.mean ?? null);
  if (value === null) return null;
  const f = formatDisplay(value, series.unit, category, settings, series.path);
  return { label, text: f.text, unit: f.unit };
}

// The chart's title moved to @neomaris/protocol (task neo_board), so the cloud
// names a pinned chart exactly as this page titles it.
export { neoChartTitle };

const HOUR_MS = 3_600_000;
const DAY_MS = 24 * HOUR_MS;

function atLocalMidnight(ms: number): boolean {
  const d = new Date(ms);
  return (
    d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0 && d.getMilliseconds() === 0
  );
}

/**
 * The window in words: "last 24 h" / "last 7 days" for a window that ended when
 * Neo answered, else its dates in the viewer's locale and time zone. A window of
 * whole local days names its days — "Sep 27 – Sep 28", or one "Sep 28" — never
 * the midnight it ends on, which would read as one day more.
 */
export function neoChartWindowLabel(window: NeoChartWindow, locale?: string): string {
  const from = Date.parse(window.from);
  const to = Date.parse(window.to);
  const span = to - from;
  if (window.live) {
    if (span < 48 * HOUR_MS) return `last ${Math.max(1, Math.round(span / HOUR_MS))} h`;
    return `last ${Math.round(span / DAY_MS)} days`;
  }
  const days = new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' });
  if (atLocalMidnight(from) && atLocalMidnight(to)) {
    const first = days.format(from);
    const last = days.format(to - 1);
    return first === last ? first : `${first} – ${last}`;
  }
  if (span >= 2 * DAY_MS) return `${days.format(from)} – ${days.format(to)}`;
  const f = new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  return `${f.format(from)} – ${f.format(to)}`;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * A bar's label from its local calendar key (YYYY-MM-DD): the day of the month
 * for a day — fourteen of them fit the chat panel — and "21 Sep" for a week.
 */
export function barLabel(key: string, by: 'day' | 'week'): string {
  const [, m, d] = key.split('-').map(Number);
  if (by === 'day') return String(d);
  return `${d} ${MONTHS[(m ?? 1) - 1]}`;
}

/** A bar value in the viewer's units: hours stay hours; distance follows the distance setting. */
export function barValue(
  value: number,
  unit: 'h' | 'nm',
  settings: UnitSettings
): { value: number; unit: string } {
  if (unit === 'h') return { value, unit: 'h' };
  return toDisplay(value * 1852, 'm', 'distance', settings);
}

/**
 * How old a readout's newest sample is — "2 min ago" — said plainly as stale
 * or old when it is (freshness from the server's own rule), or null without a
 * sample. Measured from now, so a chart reread days later says so.
 */
export function readoutAgeLabel(
  sampledAt: string | null,
  freshness: 'fresh' | 'stale' | 'old' | 'future' | null,
  nowMs: number = Date.now()
): string | null {
  if (sampledAt === null) return null;
  const s = Math.max(0, Math.round((nowMs - Date.parse(sampledAt)) / 1000));
  const m = Math.round(s / 60);
  const h = Math.round(m / 60);
  const ago =
    s < 90
      ? `${s} s ago`
      : m < 90
        ? `${m} min ago`
        : h < 36
          ? `${h} h ago`
          : `${Math.round(h / 24)} d ago`;
  return freshness === 'stale' || freshness === 'old' ? `${freshness} · ${ago}` : ago;
}
